/**
 * Author: Romain Hapi Sr.
 * Version: 1.0
 * Date: 2026-06-25
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
/**
 * Page « Carrieres » du site UK, avec le formulaire de candidature.
 *
 * `APPLY_I18N` porte les libelles du formulaire en local plutot que dans
 * `public/data/site.*.json` : ce sont des chaines d'interface propres a cette
 * boite de dialogue, et les sortir du fichier de contenu evite de le gonfler.
 *
 * Le CV part en piece jointe vers le backend, qui le transmet par courriel sans
 * le stocker — sauf si l'envoi echoue, auquel cas il est conserve en base
 * plutot que perdu.
 */
import React, { useState } from "react";
import { useLang } from "../i18n/LanguageContext";
import SEO from "../components/SEO";
import MEDIA from "../lib/media";
import { Briefcase, MapPin, Sparkles, Globe2, GraduationCap, Clock, Paperclip, Send } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { submitCareerApplication } from "../services/api";

const WHY_ICONS = [Sparkles, Globe2, GraduationCap, Clock];

const APPLY_I18N = {
  fr: {
    apply: "Postuler",
    dialogTitle: "Postuler",
    firstName: "Prénom",
    lastName: "Nom",
    coverLetter: "Lettre de motivation",
    coverLetterPlaceholder: "Parlez-nous de vous et de votre motivation…",
    cv: "CV",
    cvHint: "PDF, DOC, DOCX — max 5 Mo",
    chooseFile: "Choisir un fichier…",
    send: "Envoyer ma candidature",
    sending: "Envoi en cours…",
    success: "Candidature envoyée ! Nous vous contacterons bientôt.",
    notDelivered:
      "Votre candidature a bien été enregistrée, mais notre alerte interne n'est pas partie. Merci de nous écrire directement à hello@prenim.co.uk.",
    error: "Une erreur est survenue. Réessayez ou écrivez-nous à hello@prenim.co.uk.",
  },
  en: {
    apply: "Apply",
    dialogTitle: "Apply",
    firstName: "First name",
    lastName: "Last name",
    coverLetter: "Cover letter",
    coverLetterPlaceholder: "Tell us about yourself and your motivation…",
    cv: "CV / Resume",
    cvHint: "PDF, DOC, DOCX — max 5 MB",
    chooseFile: "Choose a file…",
    send: "Send my application",
    sending: "Sending…",
    success: "Application sent! We'll be in touch soon.",
    notDelivered:
      "Your application was saved, but our internal alert did not go out. Please email us directly at hello@prenim.co.uk.",
    error: "Something went wrong. Please try again or email hello@prenim.co.uk.",
  },
  es: {
    apply: "Postular",
    dialogTitle: "Postular",
    firstName: "Nombre",
    lastName: "Apellido",
    coverLetter: "Carta de motivación",
    coverLetterPlaceholder: "Cuéntenos sobre usted y su motivación…",
    cv: "CV",
    cvHint: "PDF, DOC, DOCX — máx 5 MB",
    chooseFile: "Elegir un archivo…",
    send: "Enviar mi candidatura",
    sending: "Enviando…",
    success: "¡Candidatura enviada! Nos pondremos en contacto pronto.",
    notDelivered:
      "Su candidatura se ha guardado, pero nuestra alerta interna no se ha enviado. Escríbanos directamente a hello@prenim.co.uk.",
    error: "Ha ocurrido un error. Inténtelo de nuevo o escríbanos a hello@prenim.co.uk.",
  },
  ar: {
    apply: "تقدم",
    dialogTitle: "تقدم للوظيفة",
    firstName: "الاسم الأول",
    lastName: "اسم العائلة",
    coverLetter: "رسالة تحفيزية",
    coverLetterPlaceholder: "أخبرنا عن نفسك ودوافعك…",
    cv: "السيرة الذاتية",
    cvHint: "PDF أو DOC أو DOCX — الحجم الأقصى 5 ميغابايت",
    chooseFile: "اختر ملفاً…",
    send: "إرسال طلبي",
    sending: "جارٍ الإرسال…",
    success: "تم إرسال طلبك! سنتواصل معك قريباً.",
    notDelivered:
      "تم حفظ طلبك، لكن التنبيه الداخلي لدينا لم يُرسل. يُرجى مراسلتنا مباشرة على hello@prenim.co.uk.",
    error: "حدث خطأ ما. يرجى المحاولة مرة أخرى أو مراسلتنا على hello@prenim.co.uk.",
  },
};

const EMPTY_FORM = { firstName: "", lastName: "", email: "", phone: "", coverLetter: "" };

const Careers = () => {
  const { t, lang } = useLang();
  const [selectedJob, setSelectedJob] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [cvFile, setCvFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  if (!t) return null;

  const meta = t.meta.pages.careers;
  const i18n = APPLY_I18N[lang] || APPLY_I18N.en;

  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleFileChange = (e) => setCvFile(e.target.files[0] || null);

  const handleClose = () => {
    setSelectedJob(null);
    setFormData(EMPTY_FORM);
    setCvFile(null);
  };

  const handleApply = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await submitCareerApplication({
        jobId: selectedJob.title,
        ...formData,
        cv: cvFile,
      });
      // The CV travels as an attachment on that email. When it cannot be sent,
      // nobody is alerted - the application is kept, CV included, but saying
      // "application sent" would leave the candidate waiting for nothing.
      if (result?.email_sent === false) {
        toast.warning(i18n.notDelivered, { duration: 10000 });
      } else {
        toast.success(i18n.success);
      }
      handleClose();
    } catch (err) {
      // The API rejects an oversized CV with 413 and an explicit message;
      // showing it beats a generic error the candidate cannot act on.
      toast.error(err?.response?.data?.detail || i18n.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="careers-page" className="pt-20">
      <SEO title={meta.title} description={meta.description} lang={lang} />

      <section className="relative bg-slate-950 text-white py-24 lg:py-32 overflow-hidden">
        <div
          className="absolute inset-0 opacity-25 bg-cover bg-center"
          style={{ backgroundImage: `url('${MEDIA.african_tech_team_2}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 to-slate-950/60" />
        <div className="relative max-w-site mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs font-semibold uppercase tracking-widest text-cyan-400">
            {t.careers.hero.eyebrow}
          </span>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl">
            {t.careers.hero.title}
          </h1>
          <p className="mt-6 text-lg lg:text-xl text-slate-300 max-w-2xl">{t.careers.hero.subtitle}</p>
        </div>
      </section>

      <section data-testid="careers-why" className="py-24 lg:py-28">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-950 mb-14">
            {t.careers.why_join.title}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.careers.why_join.items.map((item, i) => {
              const Icon = WHY_ICONS[i] || Sparkles;
              return (
                <div
                  key={i}
                  data-testid={`careers-why-${i}`}
                  className="rounded-2xl border border-slate-200 p-7 card-lift"
                >
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-amber-50 text-amber-600 mb-5">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-950 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{item.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section data-testid="careers-positions" className="py-24 lg:py-28 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-950 mb-12">
            {t.common.open_positions}
          </h2>
          <div className="space-y-4">
            {t.careers.positions.map((pos, i) => (
              <div
                key={i}
                data-testid={`careers-position-${i}`}
                className="bg-white rounded-2xl border border-slate-200 p-7 hover:border-blue-500 hover:shadow-md transition-all"
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Briefcase className="h-4 w-4 text-blue-600" />
                      <span className="text-xs font-semibold uppercase tracking-widest text-blue-600">{pos.type}</span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-950">{pos.title}</h3>
                    <p className="flex items-center gap-1.5 text-sm text-slate-500 mt-1">
                      <MapPin className="h-3.5 w-3.5" />
                      {pos.location}
                    </p>
                    <p className="mt-4 text-slate-600 leading-relaxed">{pos.description}</p>
                  </div>
                  <button
                    onClick={() => setSelectedJob(pos)}
                    data-testid={`careers-apply-${i}`}
                    className="shrink-0 inline-flex items-center justify-center rounded-full bg-slate-950 hover:bg-blue-600 text-white font-semibold px-5 py-2.5 text-sm transition-colors"
                  >
                    {i18n.apply}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold">{t.careers.cta.title}</h2>
          <p className="mt-3 text-blue-100 text-lg">{t.careers.cta.body}</p>
          <a
            href="mailto:hello@prenim.co.uk?subject=Candidature spontanée"
            data-testid="careers-spontaneous-cta"
            className="inline-flex items-center gap-2 mt-8 rounded-full bg-white text-blue-700 hover:bg-amber-300 hover:text-slate-950 font-semibold px-7 py-3.5 transition-colors"
          >
            {t.careers.cta.button}
          </a>
        </div>
      </section>

      {/* Application dialog */}
      <Dialog open={!!selectedJob} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-xl">{selectedJob?.title}</DialogTitle>
            <DialogDescription>{i18n.dialogTitle}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleApply} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  {i18n.firstName} *
                </label>
                <Input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  placeholder="Jane"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  {i18n.lastName} *
                </label>
                <Input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  placeholder="Doe"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                {t.contact.form.email}
              </label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="jane@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                {t.contact.form.phone}
              </label>
              <Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+44 7700 900000"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                {i18n.coverLetter}
              </label>
              <Textarea
                name="coverLetter"
                value={formData.coverLetter}
                onChange={handleChange}
                required
                rows={4}
                placeholder={i18n.coverLetterPlaceholder}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                {i18n.cv}{" "}
                <span className="text-slate-400 font-normal">({i18n.cvHint})</span>
              </label>
              <label className="flex items-center gap-3 px-4 py-3 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                <Paperclip className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-sm text-slate-600 truncate">
                  {cvFile ? cvFile.name : i18n.chooseFile}
                </span>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={handleFileChange}
                  className="sr-only"
                />
              </label>
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-slate-950 hover:bg-blue-600 text-white"
            >
              <Send className="mr-2 w-4 h-4" />
              {submitting ? i18n.sending : i18n.send}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default Careers;
